import { useCallback, useEffect, useRef, useState } from "react"

export type ScanPhase = "scanning" | "popup" | "done"

export interface ScanTimelineState {
  /** Index of the active stage (0-based). */
  stageIndex: number
  /** Progress 0→1 of the active stage's storyboard. */
  stageProgress: number
  /** "scanning" while a stage plays, "popup" when it has finished and waits for
   *  the user, "done" once the last popup is dismissed. */
  phase: ScanPhase
  /** Dismiss the current stage's popup and start the next stage (or finish). */
  resume: () => void
}

/**
 * Drives the Astro "Scanning" timeline: N stages, each played over
 * `stageDurations[i]` ms with a linear 0→1 progress. The duration is the single
 * speed knob — larger = slower — and the storyboard always plays through in full,
 * because every visual is positioned off `stageProgress`, not off a fixed clock.
 *
 * When a stage's progress reaches 1 the timeline switches to `phase: "popup"` and
 * freezes (the rAF loop stops) until {@link ScanTimelineState.resume} is called;
 * resume advances to the next stage, or fires `onFinish` after the last one.
 *
 * Pass a stable `stageDurations` reference (module constant or memoized) so the
 * ticking effect isn't torn down every render.
 */
export function useScanTimeline(
  stageDurations: readonly number[],
  onFinish: () => void,
): ScanTimelineState {
  const [stageIndex, setStageIndex] = useState(0)
  const [stageProgress, setStageProgress] = useState(0)
  const [phase, setPhase] = useState<ScanPhase>("scanning")

  const onFinishRef = useRef(onFinish)
  useEffect(() => {
    onFinishRef.current = onFinish
  })

  // rAF ticker for the active stage. Runs only while scanning; reaching 100%
  // hands off to the popup and stops the loop (the scan "freezes").
  useEffect(() => {
    if (phase !== "scanning") return
    // Guard the divisor so a zero/missing duration can't divide-by-zero; the
    // tick (an rAF callback, not the effect body) then drives progress to 1.
    const duration = Math.max(stageDurations[stageIndex] ?? 0, 1)

    let raf = 0
    let start: number | null = null
    const tick = (t: number) => {
      if (start === null) start = t
      const p = Math.min((t - start) / duration, 1)
      setStageProgress(p)
      if (p >= 1) {
        setPhase("popup")
        return
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [phase, stageIndex, stageDurations])

  const resume = useCallback(() => {
    if (phase !== "popup") return
    const next = stageIndex + 1
    if (next >= stageDurations.length) {
      setPhase("done")
      onFinishRef.current()
      return
    }
    setStageIndex(next)
    setStageProgress(0)
    setPhase("scanning")
  }, [phase, stageIndex, stageDurations.length])

  return { stageIndex, stageProgress, phase, resume }
}
